import axios, { AxiosInstance } from "axios";

const instance: AxiosInstance = axios.create({
    baseURL: `${import.meta.env.VITE_BFF_PROXY}/api`,
    withCredentials: true,
    headers: {
        "X-CSRF": "1",
    },
});

export const get = async (url: any) => {
    return await instance.get(url);
};

instance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { response } = error;
        console.error(error);

        if (!response) {
            console.error("Network error or no response received.");
            return Promise.reject(error);
        }

        const status = response.status;

        if ([400, 401, 403, 404].includes(status)) {
            try {
                if (
                    error.request.responseUrl?.includes("customisation/get") &&
                    status === 400
                ) {
                    return Promise.reject(error);
                }

                // Check session with BFF only for 401/403 (auth-related errors)
                if ([401, 403].includes(status)) {
                    try {
                        const sessionCheck = await axios.get(
                            `${import.meta.env.VITE_BFF_URI}/bff/session`,
                            {
                                withCredentials: true,
                                headers: {
                                    "X-CSRF": "1",
                                },
                            }
                        );

                        // Validate session is active
                        const claims = Array.isArray(sessionCheck.data) ? sessionCheck.data : [];
                        const isSessionExpired = claims.length === 0;
                        
                        if (isSessionExpired) {
                            console.warn("Session expired - redirecting to signout");
                            window.location.href = "/signout";
                        }
                        // If session is valid, still reject the original error (401/403 stands)
                    } catch (sessionErr) {
                        console.error("Session validation failed:", sessionErr);
                        // If session check fails, assume invalid session
                        window.location.href = "/signout";
                    }
                }
                // For 400/404, don't attempt session check - these are likely legitimate request errors
            } catch (checkErr) {
                console.error("Error handling failed", checkErr);
                return Promise.reject(error);
            }
        }
        return Promise.reject(error);
    }
);

export const getWithHeaders = async (url: any, headers: any) => {
    return await instance.get(url, {
        headers: {
            ...headers,
        },
    });
};

export const getWithParams = async (url: any, params: any) => {
    return await instance.get(url, {
        params: {
            ...params,
        },
    });
};

export const post = async (url: any, data: any) => {
    return await instance.post(url, data);
};

export const postWithHeaders = async (url: any, data: any, headers: any) => {
    return await instance.post(url, data, {
        headers: {
            ...headers,
        },
    });
};

export const postWithHeadersSetTimeout = async (
    url: any,
    data: any,
    headers: any,
    timeout: number
) => {
    return await instance.post(url, data, {
        headers: {
            ...headers,
        },
        timeout: timeout,
    });
};

export const put = async (url: any, data: any) => {
    return await instance.put(url, data);
};

export const httpDelete = async (url: any, data: any) => {
    return await instance.delete(url, data);
};

export const getBlobRequest = async (url: string) => {
    return await instance.get(url, { responseType: "blob" });
};
